import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { syncAllCoursesWithProgress } from '@/services/courseDataSyncService';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Database, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

interface SyncProgress {
  status: 'idle' | 'syncing' | 'completed' | 'error';
  totalCourses: number;
  syncedCourses: number;
  totalModules: number;
  syncedModules: number;
  currentCategory: string;
  errors: string[];
  categoryResults: Record<string, {
    coursesSynced: number;
    modulesSynced: number;
    status: string;
  }>;
}

interface VerificationResults {
  contentCount: number;
  modulesCount: number;
  coursesCount: number;
}

const ContentSyncDashboard: React.FC = () => {
  const [syncProgress, setSyncProgress] = useState<SyncProgress>({
    status: 'idle',
    totalCourses: 0,
    syncedCourses: 0,
    totalModules: 0,
    syncedModules: 0,
    currentCategory: '',
    errors: [],
    categoryResults: {}
  });

  const [verificationResults, setVerificationResults] = useState<VerificationResults | null>(null);

  const handleSync = async () => {
    setSyncProgress(prev => ({ 
      ...prev, 
      status: 'syncing',
      errors: [],
      categoryResults: {}
    }));
    setVerificationResults(null);
    
    toast.info('Starting database sync for all courses...');
    
    try {
      const result = await syncAllCoursesWithProgress((progress) => {
        // Transform categoryResults if present
        const { categoryResults, ...rest } = progress;
        if (categoryResults) {
          const transformedResults: Record<string, { coursesSynced: number; modulesSynced: number; status: string }> = {};
          Object.entries(categoryResults).forEach(([key, value]) => {
            transformedResults[key] = {
              coursesSynced: value.coursesSynced,
              modulesSynced: value.modulesSynced,
              status: value.success ? 'success' : 'error'
            };
          });
          setSyncProgress(prev => ({ ...prev, ...rest, categoryResults: transformedResults }));
        } else {
          setSyncProgress(prev => ({ ...prev, ...rest }));
        }
      });
      
      // Transform categoryResults to match component interface
      const transformedResults: Record<string, { coursesSynced: number; modulesSynced: number; status: string }> = {};
      Object.entries(result.categoryResults).forEach(([key, value]) => {
        transformedResults[key] = {
          coursesSynced: value.coursesSynced,
          modulesSynced: value.modulesSynced,
          status: value.success ? 'success' : 'error'
        };
      });
      
      setSyncProgress(prev => ({ 
        ...prev, 
        status: 'completed',
        totalCourses: result.totalCourses,
        totalModules: result.totalModules,
        errors: result.errors,
        categoryResults: transformedResults
      }));
      
      // Run verification
      await verifyDatabaseCounts();
      
      toast.success(`Sync complete! ${result.totalModules} modules synced across ${result.totalCourses} courses.`);
    } catch (error) {
      setSyncProgress(prev => ({ 
        ...prev, 
        status: 'error',
        errors: [...prev.errors, error instanceof Error ? error.message : 'Unknown error']
      }));
      toast.error('Sync failed. Check error log for details.');
    }
  };

  const verifyDatabaseCounts = async () => {
    try {
      const [content, modules, courses] = await Promise.all([
        supabase.from('module_content').select('*', { count: 'exact', head: true }),
        supabase.from('course_modules').select('*', { count: 'exact', head: true }),
        supabase.from('courses').select('*', { count: 'exact', head: true })
      ]);
      
      setVerificationResults({
        contentCount: content.count || 0,
        modulesCount: modules.count || 0,
        coursesCount: courses.count || 0
      });
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Failed to verify database counts');
    }
  };

  const progressPercentage = syncProgress.totalModules > 0 
    ? Math.round((syncProgress.syncedModules / syncProgress.totalModules) * 100)
    : 0;

  const categoryKeys = Object.keys(syncProgress.categoryResults);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Content Sync Dashboard
        </CardTitle>
        <CardDescription>
          Sync all 669 modules from TypeScript files to the database with real-time progress tracking.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Sync Button */}
        <Button 
          onClick={handleSync} 
          disabled={syncProgress.status === 'syncing'}
          className="w-full"
          size="lg"
        >
          {syncProgress.status === 'syncing' ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Syncing Content...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Sync All Content to Database
            </>
          )}
        </Button>

        {/* Progress Bar */}
        {syncProgress.status === 'syncing' && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Overall Progress</span>
              <span className="font-medium">{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
            {syncProgress.currentCategory && (
              <p className="text-sm text-muted-foreground">
                Syncing: <span className="font-medium">{syncProgress.currentCategory}</span>
              </p>
            )}
          </div>
        )}

        {/* Live Statistics */}
        {(syncProgress.status === 'syncing' || syncProgress.status === 'completed') && (
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="text-2xl font-bold">{syncProgress.syncedCourses}</div>
              <div className="text-sm text-muted-foreground">Courses Synced</div>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="text-2xl font-bold">{syncProgress.syncedModules}</div>
              <div className="text-sm text-muted-foreground">Modules Synced</div>
            </div>
          </div>
        )}

        {/* Category Breakdown */}
        {categoryKeys.length > 0 && (
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="categories">
              <AccordionTrigger>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Category Breakdown ({categoryKeys.length} categories)
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {categoryKeys.map((category) => {
                    const result = syncProgress.categoryResults[category];
                    return (
                      <div key={category} className="flex items-center justify-between p-2 rounded bg-muted/30">
                        <span className="text-sm font-medium">{category}</span>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{result.coursesSynced} courses</span>
                          <span>{result.modulesSynced} modules</span>
                          {result.status === 'success' ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* Error Log */}
        {syncProgress.errors.length > 0 && (
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="errors">
              <AccordionTrigger>
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  Error Log ({syncProgress.errors.length} errors)
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {syncProgress.errors.map((error, index) => (
                    <div key={index} className="text-xs text-red-600 dark:text-red-400 p-2 rounded bg-red-50 dark:bg-red-950/20">
                      {error}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* Verification Results */}
        {verificationResults && syncProgress.status === 'completed' && (
          <Alert>
            <CheckCircle className="h-4 w-4 text-green-500" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">Database Verification Complete:</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="font-medium">module_content</div>
                    <div className="text-muted-foreground">{verificationResults.contentCount} rows</div>
                  </div>
                  <div>
                    <div className="font-medium">course_modules</div>
                    <div className="text-muted-foreground">{verificationResults.modulesCount} rows</div>
                  </div>
                  <div>
                    <div className="font-medium">courses</div>
                    <div className="text-muted-foreground">{verificationResults.coursesCount} rows</div>
                  </div>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Success Message */}
        {syncProgress.status === 'completed' && syncProgress.errors.length === 0 && (
          <Alert>
            <CheckCircle className="h-4 w-4 text-green-500" />
            <AlertDescription>
              Successfully synced {syncProgress.totalModules} modules across {syncProgress.totalCourses} courses! 
              All content is now available in the database.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default ContentSyncDashboard;
